<template>
  <ConfirmModal
    persist
    :approve="t('ProspectsCatalog.createListModal.title')"
    wrapper-class="w-[100%] max-w-xl"
    data-testid="prospects-new-list-modal"
    @close-dialog="handleForm"
  >
    <template #title>
      <h1 class="p-6 pb-5 text-headline-m">{{ t('ProspectsCatalog.createListModal.title') }}</h1>
    </template>
    <template #content>
      <div class="p-6">
        <ProspectListForm ref="formRef" :prospect-list="{}" :event="props.event" @submit="handleSubmit" />
      </div>
    </template>
  </ConfirmModal>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import { useCreateProspectListMutation } from '@/gql/myomr'
import type { MeQuery, ProspectListsQuery } from '@/gql/myomr'
import { toPlainId } from '@/helpers/globalIdHelper'
import { showNotification } from '@/helpers/notificationHelper'

import ProspectListForm from './ProspectListForm.vue'

const { t } = useI18n()

const router = useRouter()

const { mutate: createProspectListMutation } = useCreateProspectListMutation()

const formRef = ref()

const props = defineProps<{
  me: NonNullable<MeQuery['me']>
  event: NonNullable<ProspectListsQuery['prospectLists']>['nodes'][number]['event']
}>()

const emit = defineEmits<{
  (e: 'refetchProspectLists'): void
}>()

function handleError() {
  showNotification(t('ProspectsCatalog.createListModal.failedNotification'), 'error', 3000)
  router.push({ name: 'prospectLists' })
}

async function handleForm(shouldSubmit: boolean) {
  if (!shouldSubmit) return router.push({ query: { modal: undefined } })

  formRef.value.submit()
}

async function handleSubmit(formData: { listName: string; contactName: string; contactEmail: string; contactReason: string }) {
  if (!props.me?.partnerCompany?.id) return

  try {
    const result = await createProspectListMutation({
      eventId: props.event.id,
      partnerCompanyId: props.me.partnerCompany.id,
      contactName: formData.contactName,
      contactEmail: formData.contactEmail,
      contactReason: formData.contactReason,
      title: formData.listName,
    })
    if (result?.data?.createProspectList?.prospectList?.id) {
      showNotification(t('ProspectsCatalog.createListModal.successfulNotification'), 'success', 3000)
      const listId = toPlainId(result.data.createProspectList.prospectList.id) as string
      router.push({ name: 'prospectList', params: { listId } })
    } else {
      handleError()
    }
  } catch (_e) {
    handleError()
  }
  emit('refetchProspectLists')
}
</script>
